"use client";

import { useEffect, useRef } from "react";

/**
 * The header's banner stack, and the SINGLE writer of `--banner-h`.
 *
 * `<main>` reserves the fixed header's height as padding-top, and `PageHero`
 * cancels exactly that padding with a mirrored negative margin so a hero image
 * runs full-bleed under the header. Both expressions include `--banner-h`, so
 * whatever occupies the banner slot MUST be inside the element measured into
 * that variable — otherwise the hero's negative margin over-pulls by the
 * unmeasured height and paints on top of it.
 *
 * That is exactly how the closure notice shipped invisible: it rendered as the
 * first child of `<main>`, inside the padding box, unmeasured, and every page
 * with a `PageHero` drew the hero straight over it. It was visible only on the
 * handful of pages with no hero — `/checkout`, `/privacy`, `/terms`, `/track`.
 *
 * So the rule is structural, not stylistic: a bar that sits above the nav goes
 * in HERE, as a child of the measured wrapper. Adding one elsewhere reopens the
 * same bug.
 *
 * Measuring the WRAPPER rather than any individual bar also fixes a latent
 * issue in the previous arrangement, where `AnnouncementBar` measured itself
 * and hardcoded `--banner-h: 0px` on dismissal. With two bars sharing the slot
 * that zero would be a lie — the closure bar still occupies space. A
 * ResizeObserver on the wrapper reports the true remaining height for free.
 */
export function HeaderBanners({
  announcement,
  closureNotice,
}: {
  /** `<AnnouncementBar/>` — renders null when there is nothing to announce. */
  announcement?: React.ReactNode;
  /**
   * `<ClosureNoticeBar/>` — an async SERVER component, passed down as a node
   * from `app/(main)/layout.tsx` so it keeps its server-side R2 read while
   * living inside this client component. Renders null when no closure is
   * announced, and is deliberately omitted on `/checkout`, where the step-4
   * notice states the same closure narrowed to the actual cart.
   */
  closureNotice?: React.ReactNode;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Measure rather than hardcode: the announcement text wraps to a second line
  // on narrow phones, and the closure bar can carry several announced periods,
  // so a fixed guess would let the header eat hero content. ResizeObserver
  // fires once on observe, so the value is correct without a separate initial
  // read, and it re-fires when either bar appears, wraps, or collapses.
  useEffect(() => {
    const el = wrapRef.current;
    const root = document.documentElement;
    if (!el) {
      root.style.setProperty("--banner-h", "0px");
      return;
    }
    const update = () =>
      root.style.setProperty("--banner-h", `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty("--banner-h", "0px");
    };
  }, []);

  return (
    <>
      <div ref={wrapRef} data-testid="header-banners">
        {announcement}
        {closureNotice}
      </div>

      {/* Sets `--banner-h` during HTML parse, before first paint, so the fixed
          header and `<main>`'s padding are laid out WITH the banners from the
          first frame — the ResizeObserver above only starts after hydration.
          Unconditional: it must also run when the announcement is absent but a
          closure bar is present, which is why it is not nested inside either
          bar's own render branch. CSP: script-src already allows 'unsafe-inline'. */}
      <script
        dangerouslySetInnerHTML={{
          __html:
            "(function(){var s=document.currentScript;var b=s&&s.previousElementSibling;if(b){document.documentElement.style.setProperty('--banner-h',b.offsetHeight+'px');}})();",
        }}
      />
    </>
  );
}
